import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  getProductBarcodeById,
  getActiveMaterialsByBarcodeId,
  getAllMaterialsByBarcodeId,
  addProductionMaterial,
  bulkAddProductionMaterials,
  updateProductionMaterial,
  deleteProductionMaterial,
  createNewBOMVersion,
} from "../../../services/Export";
import { getImportItemById } from "../../../services/Import";
import ItemSelectingPopup from "../../../components/ItemSelectingPopup";
import FormAlert from "../../../components/FormAlert";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import BuildIcon from "@mui/icons-material/Build";
import HistoryIcon from "@mui/icons-material/History";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

function ProductMaterialsManagement() {
  const theme = useTheme();
  const history = useHistory();
  const { barcodeId } = useParams();

  // State
  const [productBarcode, setProductBarcode] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openItemTable, setOpenItemTable] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const [openNewVersionDialog, setOpenNewVersionDialog] = useState(false);
  const [newVersionNotes, setNewVersionNotes] = useState("");

  // Form state
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [formData, setFormData] = useState({
    materialId: null,
    materialName: "",
    standardQtyPerUnit: "",
    notes: "",
  });
  const [itemRows, setItemRows] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  // Alert state
  const [displayAlert, setDisplayAlert] = useState(false);
  const [alertData, setAlertData] = useState({
    type: "",
    message: "",
  });

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Get product barcode info
      const barcodeResult = await getProductBarcodeById(barcodeId);
      if (barcodeResult && barcodeResult.barcodeId) {
        setProductBarcode(barcodeResult);
      }

      // Get active materials
      const activeMaterials = await getActiveMaterialsByBarcodeId(barcodeId);
      if (activeMaterials && activeMaterials !== "Something went wrong...") {
        setMaterials(activeMaterials);
      }

      // Get all materials (including inactive)
      const allMats = await getAllMaterialsByBarcodeId(barcodeId);
      if (allMats && allMats !== "Something went wrong...") {
        setAllMaterials(allMats);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showAlert("error", "Failed to load materials");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [barcodeId]);

  const showAlert = (type, message) => {
    setAlertData({ type, message });
    setDisplayAlert(true);
    setTimeout(() => setDisplayAlert(false), 5000);
  };

  const handleOpenAddDialog = () => {
    setFormData({
      materialId: null,
      materialName: "",
      standardQtyPerUnit: "",
      notes: "",
    });
    setSelectedItems([]);
    setOpenAddDialog(true);
  };

  const handleOpenEditDialog = (material) => {
    setSelectedMaterial(material);
    setFormData({
      materialId: material.materialId, // UPDATED: using DTO field
      materialName: material.materialName, // UPDATED: using DTO field
      standardQtyPerUnit: material.standardQtyPerUnit,
      notes: material.notes || "",
    });
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (material) => {
    setSelectedMaterial(material);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setSelectedMaterial(null);
    setFormData({
      materialId: null,
      materialName: "",
      standardQtyPerUnit: "",
      notes: "",
    });
  };

  const handleOpenItemTable = () => {
    setOpenItemTable(true);
  };

  const handleCloseItemTable = async () => {
    setOpenItemTable(false);
    if (selectedItems.length > 0) {
      // Get import item details
      const importItem = await getImportItemById(selectedItems[0]);
      if (importItem) {
        setFormData({
          ...formData,
          materialId: importItem.importId,
          materialName: importItem.itemName,
        });
      }
    }
  };

  const handleAddMaterial = async () => {
    if (!formData.materialId || !formData.standardQtyPerUnit) {
      showAlert("error", "Please select a material and enter quantity");
      return;
    }

    const materialBody = {
      barcodeId: parseInt(barcodeId),
      materialId: formData.materialId,
      standardQtyPerUnit: parseFloat(formData.standardQtyPerUnit),
      notes: formData.notes,
    };

    const result = await addProductionMaterial(materialBody);
    if (result) {
      showAlert("success", "Material added successfully");
      handleCloseDialogs();
      fetchData();
    } else {
      showAlert("error", "Failed to add material");
    }
  };

  const handleUpdateMaterial = async () => {
    if (!formData.standardQtyPerUnit) {
      showAlert("error", "Please enter quantity");
      return;
    }

    const materialBody = {
      standardQtyPerUnit: parseFloat(formData.standardQtyPerUnit),
      notes: formData.notes,
    };

    const result = await updateProductionMaterial(
      selectedMaterial.bomId,
      materialBody,
    );
    if (result) {
      showAlert("success", "Material updated successfully");
      handleCloseDialogs();
      fetchData();
    } else {
      showAlert("error", "Failed to update material");
    }
  };

  const handleDeleteMaterial = async () => {
    const result = await deleteProductionMaterial(selectedMaterial.bomId);
    if (result) {
      showAlert("success", "Material deleted successfully");
      handleCloseDialogs();
      fetchData();
    } else {
      showAlert("error", "Failed to delete material");
    }
  };

  const handleOpenNewVersionDialog = () => {
    setNewVersionNotes("");
    setOpenNewVersionDialog(true);
  };

  const handleCreateNewVersion = async () => {
    if (materials.length === 0) {
      showAlert(
        "error",
        "Cannot create new version without any active materials",
      );
      return;
    }

    try {
      // Prepare the bulk materials body
      const materialsBody = {
        barcodeId: parseInt(barcodeId),
        materials: materials.map((m) => ({
          materialId: m.materialId,
          standardQtyPerUnit: m.standardQtyPerUnit,
          notes: m.notes || "",
        })),
        createNewVersion: true,
      };

      const result = await createNewBOMVersion(barcodeId, materialsBody);

      if (result && result !== "Something went wrong...") {
        showAlert(
          "success",
          `New BOM version created successfully! Now at v${(productBarcode?.currentMaterialsVersion || 1) + 1}`,
        );
        setOpenNewVersionDialog(false);
        setNewVersionNotes("");
        fetchData(); // Refresh to show new version
      } else {
        showAlert("error", "Failed to create new BOM version");
      }
    } catch (error) {
      console.error("Error creating new version:", error);
      showAlert("error", "Failed to create new BOM version");
    }
  };

  const displayedMaterials = showInactive ? allMaterials : materials;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Loading materials...</Typography>
      </Box>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 3 },
        m: { xs: 1, md: 3 },
        borderRadius: 2,
        bgcolor: "#f9fafb",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <BuildIcon fontSize="large" color="primary" />
          <Box>
            <Typography variant="h5" fontWeight="600" color="primary.main">
              Production Materials (BOM)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {productBarcode?.productName} - {productBarcode?.barcode}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Back to Product">
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push(`/template/product/${barcodeId}`)}
            >
              Back
            </Button>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton
              onClick={() => history.goBack()}
              aria-label="close"
              sx={{
                bgcolor: alpha(theme.palette.grey[500], 0.1),
                "&:hover": {
                  bgcolor: alpha(theme.palette.grey[500], 0.2),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Alert */}
      {displayAlert && (
        <Box sx={{ mb: 2 }}>
          <FormAlert type={alertData.type} message={alertData.message} />
        </Box>
      )}

      {/* Summary Card */}
      <Card elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Total Materials
              </Typography>
              <Typography variant="h6" fontWeight="600" color="primary.main">
                {materials.length}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Current BOM Version
              </Typography>
              <Typography variant="h6" fontWeight="600" color="info.main">
                v{productBarcode?.currentMaterialsVersion || 1}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Inactive Materials
              </Typography>
              <Typography variant="h6" fontWeight="600" color="warning.main">
                {allMaterials.length - materials.length}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            Add Material
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<HistoryIcon />}
            onClick={handleOpenNewVersionDialog}
          >
            Create New BOM Version
          </Button>
        </Box>
        <Button
          variant="outlined"
          startIcon={<HistoryIcon />}
          onClick={() => setShowInactive(!showInactive)}
        >
          {showInactive ? "Show Active Only" : "Show All (Including Inactive)"}
        </Button>
      </Box>

      {/* Materials Table */}
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
              <TableCell>
                <strong>Material Name</strong>
              </TableCell>
              <TableCell>
                <strong>Brand</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Qty per Unit</strong>
              </TableCell>
              <TableCell>
                <strong>Unit Type</strong>
              </TableCell>
              <TableCell>
                <strong>Category</strong>
              </TableCell>
              <TableCell>
                <strong>Status</strong>
              </TableCell>
              <TableCell>
                <strong>Notes</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedMaterials.length > 0 ? (
              displayedMaterials.map((material, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    },
                    opacity: material.isActive ? 1 : 0.6,
                  }}
                >
                  <TableCell>{material.materialName}</TableCell>
                  <TableCell>{material.materialBrand || "N/A"}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={material.standardQtyPerUnit}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{material.materialUnitType || "units"}</TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {material.materialCategory || "-"}
                      {material.materialSubCat1 &&
                        ` / ${material.materialSubCat1}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={material.isActive ? "Active" : "Inactive"}
                      size="small"
                      color={material.isActive ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {material.notes || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {material.isActive && (
                      <>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(material)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(material)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No materials added yet. Click "Add Material" to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Material Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseDialogs}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Material to BOM</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleOpenItemTable}
                >
                  {formData.materialName
                    ? formData.materialName
                    : "Select Material"}
                </Button>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Standard Qty per Unit"
                  type="number"
                  value={formData.standardQtyPerUnit}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      standardQtyPerUnit: e.target.value,
                    })
                  }
                  onWheel={(e) => e.target.blur()}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes (Optional)"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleAddMaterial}
          >
            Add Material
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Material Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseDialogs}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Material</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Box
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Typography variant="caption" color="warning.dark">
                <strong>Note:</strong> You are editing the current active
                version (v
                {productBarcode?.currentMaterialsVersion || 1}). Changes will
                affect new productions. To preserve history, consider creating a
                new version instead.
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Material Name"
                  value={formData.materialName}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Standard Qty per Unit"
                  type="number"
                  value={formData.standardQtyPerUnit}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      standardQtyPerUnit: e.target.value,
                    })
                  }
                  onWheel={(e) => e.target.blur()}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes (Optional)"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleUpdateMaterial}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDialogs}>
        <DialogTitle>Delete Material</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this material from the BOM?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Material: <strong>{selectedMaterial?.materialName}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteMaterial}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create New Version Dialog */}
      <Dialog
        open={openNewVersionDialog}
        onClose={() => setOpenNewVersionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">Create New BOM Version</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Creating a new version will:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 2 }}>
              <li>
                <Typography variant="body2">
                  Increment version from v
                  {productBarcode?.currentMaterialsVersion || 1} to v
                  {(productBarcode?.currentMaterialsVersion || 1) + 1}
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Mark all current materials as inactive (preserving history)
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Copy current active materials to the new version
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Allow you to modify the new version independently
                </Typography>
              </li>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Current Active Materials (will be copied):
            </Typography>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ mt: 1, maxHeight: 300 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Material</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Qty/Unit</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {materials.map((material, index) => (
                    <TableRow key={index}>
                      <TableCell>{material.materialName}</TableCell>
                      <TableCell align="right">
                        {material.standardQtyPerUnit}{" "}
                        {material.materialUnitType}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TextField
              fullWidth
              label="Version Notes (Optional)"
              placeholder="Describe what changed in this version..."
              multiline
              rows={3}
              value={newVersionNotes}
              onChange={(e) => setNewVersionNotes(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewVersionDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleCreateNewVersion}
          >
            Create New Version
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item Selection Popup */}
      {openItemTable && (
        <ItemSelectingPopup
          setOpenTable={setOpenItemTable}
          openTable={openItemTable}
          handleCloseTable={handleCloseItemTable}
          setSelectedItems={setSelectedItems}
          selectedItems={selectedItems}
          setRows={setItemRows}
          rows={itemRows}
        />
      )}
    </Paper>
  );
}

export default ProductMaterialsManagement;
